import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtQty } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Wallet, TrendingUp, PiggyBank, Activity, Sparkles, Clock, CheckCircle2, X as XIcon, Loader2, Eye, Plus } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import SentimentTicker from "../components/SentimentTicker";
import MarketTrendsBoard from "../components/MarketTrendsBoard";
import OnboardingModal from "../components/OnboardingModal";
import BrokerHealthStrip from "../components/BrokerHealthStrip";

const Sparkline = ({ data, up }) => (
  <ResponsiveContainer width="100%" height={40}>
    <AreaChart data={data.map((v, i) => ({ i, v }))}>
      <defs>
        <linearGradient id={`spark-${up}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={up ? "hsl(var(--gain))" : "hsl(var(--loss))"} stopOpacity={0.4} />
          <stop offset="100%" stopColor={up ? "hsl(var(--gain))" : "hsl(var(--loss))"} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey="v" strokeWidth={1.75}
            stroke={up ? "hsl(var(--gain))" : "hsl(var(--loss))"}
            fill={`url(#spark-${up})`} />
    </AreaChart>
  </ResponsiveContainer>
);

// Compact "3m ago" / "just now" formatter for freshness badges.
function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 30) return "just now";
  if (s < 90) return "1m ago";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const orderStatusPill = (status) => {
  const s = (status || "filled").toLowerCase();
  if (s === "filled")
    return <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--gain))] bg-[hsl(var(--gain))]/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> Filled</span>;
  if (s === "cancelled" || s === "canceled" || s === "rejected")
    return <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--loss))] bg-[hsl(var(--loss))]/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><XIcon className="w-2.5 h-2.5" /> {s}</span>;
  return <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--ai))] bg-[hsl(var(--ai))]/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> {s}</span>;
};
// Suppress unused-var warnings for helpers kept in case orders come back to the dashboard later.
// eslint-disable-next-line no-unused-vars
const _keepOrderPill = orderStatusPill;

export default function Dashboard() {
  const [portfolio, setPortfolio] = useState(null);
  const [crypto, setCrypto] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [wlAdd, setWlAdd] = useState("");
  const [wlAdding, setWlAdding] = useState(false);
  const [tick, setTick] = useState(0);   // forces timeAgo re-render every 30s
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [p, c, s, w] = await Promise.allSettled([
        api.get("/portfolio"),
        api.get("/markets/crypto"),
        api.get("/markets/stocks"),
        api.get("/watchlist"),
      ]);
      if (p.status === "fulfilled") setPortfolio(p.value.data);
      if (c.status === "fulfilled") setCrypto(c.value.data);
      if (s.status === "fulfilled") setStocks(s.value.data);
      if (w.status === "fulfilled") setWatchlist(Array.isArray(w.value.data) ? w.value.data : []);
    })();
  }, []);

  // Refresh "updated Xm ago" labels every 30s without re-fetching data.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const movers = [...crypto, ...stocks].sort((a, b) => Math.abs(b.change_pct) - Math.abs(a.change_pct)).slice(0, 6);

  // Quick-add any ticker straight from the Dashboard watchlist card. Verifies
  // the symbol resolves to a real price (stock first, crypto as fallback)
  // before persisting, so typos never pollute the list.
  const quickAddWatch = async (e) => {
    e && e.preventDefault();
    const sym = wlAdd.trim().toUpperCase().replace(/\s+/g, "");
    if (!sym) return;
    if (!/^[A-Z0-9.\-]{1,12}$/.test(sym)) {
      toast.error("Ticker must be 1-12 letters/numbers");
      return;
    }
    setWlAdding(true);
    let verified = null;
    let assetType = "stock";
    try {
      const r = await api.get(`/assets/stock/${sym}`);
      if (r.data && (r.data.price != null || r.data.name)) {
        verified = r.data;
        assetType = "stock";
      }
    } catch { /* try crypto next */ }
    if (!verified) {
      try {
        const r = await api.get(`/assets/crypto/${sym.toLowerCase()}`);
        if (r.data && (r.data.price != null || r.data.name)) {
          verified = r.data;
          assetType = "crypto";
        }
      } catch { /* still unverified — we'll add as stock anyway */ }
    }
    try {
      const finalSym = assetType === "crypto" ? sym.toLowerCase() : sym;
      await api.post("/watchlist", { symbol: finalSym, asset_type: assetType });
      if (verified) {
        toast.success(`Added ${sym}`, {
          description: verified.name ? `${verified.name} · ${fmtMoney(verified.price)}` : undefined,
        });
      } else {
        toast.success(`Added ${sym}`, {
          description: "Price will populate on the next refresh.",
        });
      }
      setWlAdd("");
      const w = await api.get("/watchlist");
      setWatchlist(Array.isArray(w.data) ? w.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add symbol");
    }
    setWlAdding(false);
  };

  const stat = (label, value, sub, icon, tint) => (
    <Card className="p-6 border-border rounded-xl">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint || "bg-secondary"}`}>{icon}</div>
      </div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="font-mono-tab text-2xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 font-mono-tab">{sub}</div>}
    </Card>
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <OnboardingModal />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Dashboard</h1>
        </div>
        <div className="w-full sm:w-auto sm:min-w-[320px]">
          <BrokerHealthStrip />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stat("Total equity", fmtMoney(portfolio?.total_equity), null,
              <Wallet className="w-5 h-5" />, "bg-secondary")}
        {stat("Cash balance", fmtMoney(portfolio?.cash_balance), null,
              <PiggyBank className="w-5 h-5" />, "bg-secondary")}
        {stat("Invested", fmtMoney(portfolio?.invested_value), null,
              <TrendingUp className="w-5 h-5" />, "bg-secondary")}
        {stat("Total P&L",
              fmtMoney(portfolio?.total_pnl),
              <span className={portfolio?.total_pnl >= 0 ? "gain" : "loss"}>{fmtPct(portfolio?.total_pnl_pct)}</span>,
              portfolio?.total_pnl >= 0 ? <ArrowUpRight className="w-5 h-5 gain" /> : <ArrowDownRight className="w-5 h-5 loss" />,
              "bg-secondary")}
      </div>

      <SentimentTicker />

      {/* Watchlist snapshot — tap any row to open the asset */}
      <Card className="p-6 border-border rounded-xl" data-testid="watchlist-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-baseline gap-2">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <Eye className="w-5 h-5" /> Your watchlist
            </h2>
            {watchlist.length > 0 && (
              <span
                data-testid="watchlist-count-badge"
                className="text-[10px] uppercase tracking-widest font-medium text-muted-foreground bg-secondary/60 rounded-full px-2 py-0.5"
              >
                {watchlist.length} {watchlist.length === 1 ? "symbol" : "symbols"}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate("/watchlist")}
            data-testid="watchlist-open-btn"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Open watchlist →
          </button>
        </div>

        {/* Quick-add any ticker */}
        <form onSubmit={quickAddWatch} className="flex items-center gap-2 mb-4">
          <Input
            value={wlAdd}
            onChange={(e) => setWlAdd(e.target.value.toUpperCase())}
            placeholder="Type any ticker (e.g. SHAZ, TSLA, AAPL, BTC)"
            data-testid="dashboard-watchlist-add-input"
            maxLength={12}
            autoComplete="off"
            className="flex-1 font-mono-tab uppercase text-sm h-9 bg-secondary/30 border-border/60"
          />
          <button type="submit" disabled={wlAdding || !wlAdd.trim()}
                  data-testid="dashboard-watchlist-add-btn"
                  className="h-9 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
            <Plus className="w-3.5 h-3.5" /> {wlAdding ? "Adding…" : "Add"}
          </button>
        </form>
        {watchlist.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {watchlist.slice(0, 12).map((w) => (
              <button
                key={w.id || `${w.asset_type}-${w.symbol}`}
                onClick={() => navigate(`/asset/${w.asset_type}/${w.symbol}`)}
                data-testid={`watchlist-item-${w.symbol}`}
                className="text-left p-2.5 rounded-lg border border-border/60 hover:border-border hover:bg-secondary/40 transition-colors"
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-medium text-sm truncate">{w.ticker || w.symbol}</span>
                  <span className={`text-[10px] font-mono-tab ${(w.change_pct || 0) >= 0 ? "gain" : "loss"}`}>
                    {(w.change_pct || 0) >= 0 ? "+" : ""}{Number(w.change_pct || 0).toFixed(2)}%
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground font-mono-tab mt-0.5">
                  {w.price ? fmtMoney(w.price, w.price > 100 ? 2 : 4) : "—"}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-sm text-muted-foreground" data-testid="watchlist-empty">
            <Eye className="w-6 h-6 mx-auto mb-2 opacity-40" />
            Nothing on your watchlist yet.
            <div className="mt-2">
              <button
                onClick={() => navigate("/markets")}
                data-testid="watchlist-add-btn"
                className="inline-flex items-center gap-1 text-xs text-foreground hover:underline"
              >
                <Plus className="w-3 h-3" /> Add symbols from Markets
              </button>
            </div>
          </div>
        )}
      </Card>

      <div>
        {/* Holdings — now full-width; Recent orders lives under Trade → Orders */}
        <Card className="p-6 border-border rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-baseline gap-2">
              <h2 className="font-display text-lg font-semibold">Your holdings</h2>
              {portfolio?.holdings?.length > 0 && (
                <span
                  data-testid="positions-count-badge"
                  className="text-[10px] uppercase tracking-widest font-medium text-muted-foreground bg-secondary/60 rounded-full px-2 py-0.5"
                >
                  {portfolio.holdings.length} {portfolio.holdings.length === 1 ? "position" : "positions"}
                </span>
              )}
            </div>
            <button onClick={() => navigate("/portfolio")} className="text-xs text-muted-foreground hover:text-foreground" data-testid="view-portfolio-link">View all →</button>
          </div>
          {portfolio?.holdings?.length ? (
            <div className="space-y-2">
              {portfolio.holdings.slice(0, 6).map((h) => (
                <div key={`${h.asset_type}:${h.symbol}`}
                     onClick={() => navigate(`/asset/${h.asset_type}/${h.symbol}`)}
                     data-testid={`holding-row-${h.symbol}`}
                     className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-secondary/60 cursor-pointer">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{h.ticker}</span>
                      <span
                        data-testid={`holding-qty-${h.symbol}`}
                        className="text-[10px] font-mono-tab uppercase tracking-wider text-muted-foreground bg-secondary/60 rounded px-1.5 py-0.5 whitespace-nowrap"
                      >
                        {fmtQty(h.quantity)} {h.asset_type === "stock" ? (h.quantity === 1 ? "share" : "shares") : h.ticker}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {h.name}
                      {h.avg_cost > 0 && (
                        <span data-testid={`holding-costbasis-${h.symbol}`} className="ml-2 font-mono-tab">
                          · avg {fmtMoney(h.avg_cost, h.avg_cost > 100 ? 2 : 4)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-tab font-medium">{fmtMoney(h.value)}</div>
                    <div
                      data-testid={`holding-pnl-${h.symbol}`}
                      className={`font-mono-tab text-xs whitespace-nowrap ${h.pnl >= 0 ? "gain" : "loss"}`}
                    >
                      {h.pnl >= 0 ? "+" : "-"}{fmtMoney(Math.abs(h.pnl))} <span className="opacity-70">({fmtPct(h.pnl_pct)})</span>
                    </div>
                  </div>
                </div>
              ))}
              {portfolio.holdings.length > 6 && (
                <button
                  onClick={() => navigate("/portfolio")}
                  data-testid="holdings-see-more"
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-2 mt-1"
                >
                  + {portfolio.holdings.length - 6} more →
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-10 text-sm text-muted-foreground">
              <Activity className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No positions yet. <button className="underline" onClick={() => navigate("/markets")}>Start trading</button>
            </div>
          )}
        </Card>
      </div>

      {/* AI teaser (moved below so Recent Orders sits next to holdings) */}
      <Card className="p-5 rounded-xl ai-tint" data-testid="ai-teaser">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[hsl(var(--ai))]" />
            <div>
              <div className="font-display font-semibold">AI Coach</div>
              <p className="text-xs text-muted-foreground mt-0.5">Analysis grounded in Market Wizards & Reminiscences of a Stock Operator.</p>
            </div>
          </div>
          <button onClick={() => navigate("/ai")}
                  className="text-sm font-medium text-[hsl(var(--ai))] hover:underline"
                  data-testid="ai-teaser-cta">Ask AI Coach →</button>
        </div>
      </Card>

      {/* Market trends — sectors + gainers/losers/most active/options flow (moved from /trends) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5 opacity-70" /> Market trends
            </h2>
            <div className="text-xs text-muted-foreground mt-0.5">Live gainers, losers, dollar-volume leaders, options flow and sector rotation.</div>
          </div>
          <button
            onClick={() => navigate("/trends")}
            data-testid="trends-open-full"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Full board →
          </button>
        </div>
        <MarketTrendsBoard compact={true} />
      </div>
    </div>
  );
}
